import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { Box, Text, Button } from '@rsos/base-components';
import { ErrorComponent } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput.styles';
import { GPX_FEATURES } from '@rsos/gainsight';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  expand,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  OUTBOUND_SMS_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  useCreateConversationOutboundSMSMutation,
  useLazyGetConversationMetadataQuery,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  setSelectedConversation,
  setPendingTF911Message,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { cleanPhoneNumber } from '@rsos/utils/phoneNumberUtils';
import callQueueSelectorNext from '../../../../selectors/callQueueSelectorNext';
import {
  trackSendOutboundSMSClick,
  trackStartTF911Response,
} from '../../CallChats/textFrom911Trackings';
import {
  INVALID_NUMBER,
  MSG_REQUEST_ERROR,
  INVALID_NUMBER_ERROR,
  MESSAGE_FAILED_TO_SEND,
  NUMBER_ALREADY_CALLED_911,
  EXISTING_CONVERSATION_ERROR,
} from './constants';
import {
  initialValues,
  validationSchema,
  phoneNumberValidator,
} from './helpers';
import {
  Container,
  PhoneNumberContainer,
  SendMsgContainer,
  TextArea,
  CustomMultiEntryInput,
  TextAreaContainer,
} from './styles/OutboundSMS.styles';

const PrefixComponent = (
  <Text
    pl="6px"
    mb="5px"
    size="normal"
    color="secondaryText"
    wordBreak="keep-all"
  >
    To:
  </Text>
);

const initialErrorState = {
  [MSG_REQUEST_ERROR]: false,
  [INVALID_NUMBER_ERROR]: false,
  [NUMBER_ALREADY_CALLED_911]: false,
  [EXISTING_CONVERSATION_ERROR]: false,
};
export const OutboundSMS = () => {
  const textAreaContainerRef = useRef(null);
  const dispatch = useDispatch();
  const [phoneNumberTags, setPhoneNumberTags] = useState([]);
  const [errorMsgs, setErrorMsgs] = useState(initialErrorState);

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const filteredCalls = useSelector(state =>
    callQueueSelectorNext(state, true),
  );

  const handleChange = (e, setFieldValue) => {
    const { value } = e.target;
    setFieldValue('initialOutboundMessage', value);
    setErrorMsgs({
      ...errorMsgs,
      [MSG_REQUEST_ERROR]: false,
    });

    if (textAreaContainerRef.current?.dataset) {
      textAreaContainerRef.current.dataset.replicatedValue = value;
    }
  };

  const [createConversation, conversationResult] =
    useCreateConversationOutboundSMSMutation();
  const [getConversationMetadata] = useLazyGetConversationMetadataQuery();

  const handleSubmit = values => {
    const phoneNumberTag = values?.phoneNumberTags?.[0];
    const message = values?.initialOutboundMessage;
    const phoneNumber = cleanPhoneNumber(phoneNumberTag?.value);
    const callerID = phoneNumber?.startsWith('1')
      ? phoneNumber
      : `1${phoneNumber}`;

    const opt = {
      callerID,
      message,
      orgName,
    };

    trackSendOutboundSMSClick();

    createConversation(opt).then(resp => {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911_OUTBOUND,
        responseSuccess: !resp?.error,
        conversationId: resp?.data?.conversation_id,
      });
      if (resp?.error) {
        if (resp.error?.status === 409) {
          getConversationMetadata({
            callerID,
            orgName,
            custom: { isOutboundSMS: true },
          }).then(resp => {
            if (resp?.isSuccess) {
              dispatch(setSelectedCallerID(callerID));
              dispatch(setSelectedConversation(callerID));
              dispatch(setPendingTF911Message(message));
              dispatch(setSelectedTab('chat'));
              dispatch(closeCard(OUTBOUND_SMS_CARD));
              dispatch(expand());
            }
          });
        } else {
          setErrorMsgs({
            ...errorMsgs,
            [MSG_REQUEST_ERROR]: true,
          });
        }
      } else {
        // TODO: logic on success
        dispatch(closeCard(OUTBOUND_SMS_CARD));
        dispatch(setSelectedCallerID(callerID));
        dispatch(expand());
      }
    });
  };

  const handleUpdateTag = phoneNumberTags => {
    setPhoneNumberTags(phoneNumberTags);
    setErrorMsgs({
      ...errorMsgs,
      [INVALID_NUMBER_ERROR]: false,
      [NUMBER_ALREADY_CALLED_911]: false,
      [EXISTING_CONVERSATION_ERROR]: false,
    });
  };

  const inputError = useMemo(() => {
    if (errorMsgs[INVALID_NUMBER_ERROR]) {
      return INVALID_NUMBER;
    }
    return null;
  }, [errorMsgs]);

  const isBtnDisabled = useCallback((errors, values) => {
    return (
      errors.phoneNumberTags ||
      !values?.initialOutboundMessage?.length ||
      errors.initialOutboundMessage ||
      !values.phoneNumberTags.length
    );
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <Form id="outbound-sms">
            <Container>
              <PhoneNumberContainer>
                <Box>
                  <Text fontWeight="bold" size="large" color="primaryText">
                    Send New Message
                  </Text>
                  <Text size="normal" color="secondaryText">
                    Text a phone number that did not call 911
                  </Text>
                </Box>
                <Box width="100%" py={3}>
                  <CustomMultiEntryInput
                    value={values.phoneNumber}
                    name="phoneNumber"
                    placeholder={
                      phoneNumberTags.length ? '' : '1 (555) 555-5555'
                    }
                    tags={phoneNumberTags}
                    updateTags={handleUpdateTag}
                    setFieldValue={setFieldValue}
                    validator={phoneNumberValidator}
                    inputType="tel"
                    hiddenInputName="phoneNumberTags"
                    error={inputError ? inputError : errors?.phoneNumberTags}
                    disabled={!!phoneNumberTags.length}
                    prefixComponent={PrefixComponent}
                  />
                </Box>
              </PhoneNumberContainer>
              <SendMsgContainer minHeight="60px">
                <TextAreaContainer ref={textAreaContainerRef}>
                  <TextArea
                    rows={1}
                    data-name="outbound-textbox"
                    name="initialOutboundMessage"
                    onChange={e => handleChange(e, setFieldValue)}
                    placeholder="Message"
                    value={values.initialOutboundMessage}
                    maxHeight={104}
                    error={
                      errors?.initialOutboundMessage ||
                      errorMsgs[MSG_REQUEST_ERROR]
                    }
                    disabled={conversationResult?.isLoading}
                  />
                  {errors?.initialOutboundMessage ? (
                    <ErrorComponent error={errors.initialOutboundMessage} />
                  ) : errorMsgs[MSG_REQUEST_ERROR] ? (
                    <ErrorComponent error={MESSAGE_FAILED_TO_SEND} />
                  ) : null}
                </TextAreaContainer>
                <Button
                  dataName="outbound-submit"
                  type="submit"
                  btnType="primary"
                  form="outbound-sms"
                  mb={0}
                  mr={0}
                  disabled={isBtnDisabled(errors, values)}
                  isLoadingProp={conversationResult.isLoading}
                >
                  Send
                </Button>
              </SendMsgContainer>
            </Container>
          </Form>
        );
      }}
    </Formik>
  );
};
